import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useStores } from "@stores";
import { useSeasonsVM } from "@stores/SeasonStore";
import { useLang } from "@src/hooks/useLang";
import { AreaUnit, LengthUnit } from "@services/demosService";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import Spinner from "@components/Spinner";
import {
  getHarvestedArea,
  getAverageYield,
  DashboardDevice,
  DailyArea,
  YieldDevice,
  DailyYield,
} from "@services/dashboardService";
import { convertArea, convertLength, roundLengthForDisplay } from "@src/utils/convert";
import { isJohnDeereCoeffSource } from "./johnDeereSource";

// ── Styled Components ───────────────────────────────────────
const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

/** Desktop: two chart cards per row; mobile: one per row (see MOBILE_BREAKPOINT). */
const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  width: 100%;
  align-items: stretch;

  @media (min-width: ${MOBILE_BREAKPOINT + 1}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.2);
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px;
  }
`;

const ChartCard = styled(Card)`
  min-width: 0;
`;

const Title = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 18px;
  color: #494c5b;
  margin-bottom: 16px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const Chip = styled.button<{ active: boolean; chipColor: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 16px;
  border: 1.5px solid ${({ active, chipColor }) => (active ? chipColor : "#cbd5d8")};
  background: ${({ active, chipColor }) => (active ? chipColor + "18" : "#fff")};
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #494c5b;
  transition: all 0.15s;

  &:hover {
    border-color: ${({ chipColor }) => chipColor};
  }
`;

const ColorDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  font-weight: 700;
  color: #899093;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #f0f2f3;
`;

const Td = styled.td<{ bold?: boolean }>`
  padding: 10px 12px;
  color: #494c5b;
  font-weight: ${({ bold }) => (bold ? 700 : 400)};
  border-bottom: 1px solid #f0f2f3;
`;

const LoadingLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: Nunito Sans, sans-serif;
  font-size: 15px;
  color: #899093;
`;

// ── Color palette for devices ───────────────────────────────
const DEVICE_COLORS = [
  "#68ad18",
  "#2196F3",
  "#FF9800",
  "#9C27B0",
  "#F44336",
  "#00BCD4",
  "#795548",
  "#607D8B",
  "#E91E63",
  "#4CAF50",
];

// ── Styled extras ────────────────────────────────────────────
const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

const RangeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 8px;
  padding: 4px;
`;

const RangeButton = styled.button<{ active: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  border: none;
  background: ${({ active }) => (active ? "#68ad18" : "transparent")};
  color: ${({ active }) => (active ? "#fff" : "#494c5b")};
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${({ active }) => (active ? "#68ad18" : "#f0f7e6")};
  }
`;

const CustomDateInput = styled.input`
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  padding: 6px 8px;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #494c5b;
`;

const RangeErrorText = styled.div`
  color: #e53935;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid rgba(137, 144, 147, 0.3);
  background: #fff;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #899093;
  transition: all 0.15s;
  &:hover {
    border-color: #68ad18;
    color: #68ad18;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const UncalibratedBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 4px;
  padding: 1px 5px;
  margin-left: 4px;
  vertical-align: middle;
`;

// A John Deere combine without a GreenGrowth calibration: the mass is Deere's
// own (coefficient source "johndeere"), so it is labelled, not flagged.
const JohnDeereBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #4d7c0f;
  background: #f0f7e6;
  border: 1px solid #c5e1a5;
  border-radius: 4px;
  padding: 1px 5px;
  margin-left: 4px;
  vertical-align: middle;
  white-space: nowrap;
`;

// ── Date range selector ─────────────────────────────────────

type RangeMode = "3d" | "5d" | "7d" | "custom";
const MAX_RANGE_DAYS = 7;
const MAX_RANGE_MS = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
const PRESET_DAYS: Record<Exclude<RangeMode, "custom">, number> = {
  "3d": 3,
  "5d": 5,
  "7d": 7,
};

function toDateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ── Component ───────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const lang = useLang();
  const { settingsStore } = useStores();
  const seasonsVM = useSeasonsVM();
  void seasonsVM; // currently unused — selector drives the range
  const areaUnit = settingsStore.areaUnit;
  const areaLabel = areaUnit === AreaUnit.Acre ? "ac" : "ha";
  const massUnitLabel = settingsStore.weightUnit;
  const lengthUnit = settingsStore.lengthUnit;

  const [loading, setLoading] = useState(false);
  const [devices, setDevices] = useState<DashboardDevice[]>([]);
  const [daily, setDaily] = useState<DailyArea[]>([]);

  const [yieldLoading, setYieldLoading] = useState(false);
  const [yieldDevices, setYieldDevices] = useState<YieldDevice[]>([]);
  const [dailyYield, setDailyYield] = useState<DailyYield[]>([]);
  const [yieldUnit, setYieldUnit] = useState("");
  const [tphUnit, setTphUnit] = useState("");

  const [visibleDevices, setVisibleDevices] = useState<Set<string>>(new Set());

  // Off by default: the unfiltered totals are what this dashboard has always
  // shown, and harvest on fields that were never drawn disappears when this
  // is on — so it is something the user opts into, not a silent default.
  const [insideFieldsOnly, setInsideFieldsOnly] = useState(false);

  // ── Date range state ──────────────────────────────────────
  const [rangeMode, setRangeMode] = useState<RangeMode>("3d");
  const today = useMemo(() => new Date(), []);
  const [customFrom, setCustomFrom] = useState<string>(() =>
    toDateInputValue(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000))
  );
  const [customTo, setCustomTo] = useState<string>(() =>
    toDateInputValue(today)
  );

  const range = useMemo<{ from: string; to: string } | { error: string } | null>(() => {
    if (rangeMode !== "custom") {
      const days = PRESET_DAYS[rangeMode];
      const now = new Date();
      const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      return { from: from.toISOString(), to: now.toISOString() };
    }
    if (!customFrom || !customTo) return null;
    const f = new Date(`${customFrom}T00:00:00`);
    const t = new Date(`${customTo}T23:59:59`);
    if (!isFinite(f.getTime()) || !isFinite(t.getTime())) return null;
    if (f.getTime() >= t.getTime()) {
      return { error: lang.analytics?.rangeOrderError ?? "Start must be before end" };
    }
    if (t.getTime() - f.getTime() > MAX_RANGE_MS) {
      return {
        error:
          lang.analytics?.rangeMaxError ??
          `Range cannot exceed ${MAX_RANGE_DAYS} days`,
      };
    }
    return { from: f.toISOString(), to: t.toISOString() };
  }, [rangeMode, customFrom, customTo, lang.analytics]);

  const rangeError = range && "error" in range ? range.error : null;
  const validRange = range && "from" in range ? range : null;

  // Latest fetchData call: changing the range while a slow (e.g. 90-day)
  // request is still running must not let it land last and overwrite the new one.
  const fetchReqRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!validRange) return;
    const { from, to } = validRange;
    const req = ++fetchReqRef.current;
    const isLatest = () => req === fetchReqRef.current;

    // Sequential: area first → render → yield → render. We deliberately don't
    // clear prior data so the chart stays visible while the next range loads
    // (per-card loading indicators handle the in-flight signal).
    setLoading(true);
    try {
      const areaRes = await getHarvestedArea(from, to, insideFieldsOnly);
      if (!isLatest()) return;
      setDevices(areaRes.devices);
      setDaily(areaRes.daily);
      setVisibleDevices(new Set(areaRes.devices.map((d) => d.esn)));
    } catch (err) {
      console.error("Dashboard area fetch error:", err);
    } finally {
      if (isLatest()) setLoading(false);
    }
    if (!isLatest()) return;

    setYieldLoading(true);
    try {
      const yieldRes = await getAverageYield(
        from,
        to,
        settingsStore.units,
        insideFieldsOnly
      );
      if (!isLatest()) return;
      setYieldDevices(yieldRes.devices);
      setDailyYield(yieldRes.daily);
      setYieldUnit(yieldRes.unit);
      setTphUnit(yieldRes.tphUnit);
    } catch (err) {
      console.error("Dashboard yield fetch error:", err);
    } finally {
      if (isLatest()) setYieldLoading(false);
    }
  }, [validRange, settingsStore.units, insideFieldsOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Build color map
  const colorMap = useMemo(() => {
    const map = new Map<string, string>();
    devices.forEach((d, i) => {
      map.set(d.esn, DEVICE_COLORS[i % DEVICE_COLORS.length]);
    });
    return map;
  }, [devices]);

  // Convert area values
  const convertHa = useCallback(
    (ha: number) => {
      if (areaUnit === AreaUnit.Acre) {
        return convertArea(ha, AreaUnit.Hectare, AreaUnit.Acre);
      }
      return ha;
    },
    [areaUnit]
  );

  // Pivot daily data: { day, [deviceEsn]: area }
  const chartData = useMemo(() => {
    const dayMap = new Map<string, Record<string, number | string>>();
    for (const row of daily) {
      if (!visibleDevices.has(row.device_id)) continue;
      if (!dayMap.has(row.day)) {
        dayMap.set(row.day, { day: row.day });
      }
      const entry = dayMap.get(row.day)!;
      entry[row.device_id] = Math.round(convertHa(row.area_ha) * 100) / 100;
    }
    return Array.from(dayMap.values()).sort((a, b) =>
      (a.day as string).localeCompare(b.day as string)
    );
  }, [daily, visibleDevices, convertHa]);

  // Compute totals for visible devices in the selected period
  const deviceTotals = useMemo(() => {
    const totals = new Map<string, number>();
    for (const row of daily) {
      if (!visibleDevices.has(row.device_id)) continue;
      totals.set(
        row.device_id,
        (totals.get(row.device_id) ?? 0) + row.area_ha
      );
    }
    return devices
      .map((d) => ({
        ...d,
        totalArea: convertHa(totals.get(d.esn) ?? 0),
      }))
      .filter((d) => visibleDevices.has(d.esn))
      .sort((a, b) => b.totalArea - a.totalArea);
  }, [devices, daily, visibleDevices, convertHa]);

  const grandTotal = useMemo(
    () => deviceTotals.reduce((sum, d) => sum + d.totalArea, 0),
    [deviceTotals]
  );

  // Pivot daily yield data: { day, [deviceEsn]: yieldValue }
  // Seed with ALL days from the area chart so both charts share the same
  // x-axis (including empty days where the device moved but had no sensor data).
  const yieldChartData = useMemo(() => {
    const dayMap = new Map<string, Record<string, number | string>>();
    // Pre-populate every day that appears in the area chart
    for (const entry of chartData) {
      dayMap.set(entry.day as string, { day: entry.day });
    }
    for (const row of dailyYield) {
      if (!visibleDevices.has(row.device_id)) continue;
      if (!dayMap.has(row.day)) {
        dayMap.set(row.day, { day: row.day });
      }
      const entry = dayMap.get(row.day)!;
      entry[row.device_id] = Math.round(row.yield_value * 100) / 100;
    }
    return Array.from(dayMap.values()).sort((a, b) =>
      (a.day as string).localeCompare(b.day as string)
    );
  }, [dailyYield, visibleDevices, chartData]);

  // Map yield device info by ESN for quick lookup
  const yieldDeviceMap = useMemo(() => {
    const m = new Map<string, YieldDevice>();
    for (const d of yieldDevices) m.set(d.esn, d);
    return m;
  }, [yieldDevices]);

  const grandTotalMass = useMemo(
    () =>
      deviceTotals.reduce((sum, d) => {
        const yd = yieldDeviceMap.get(d.esn);
        return sum + (yd?.totalMass ?? 0);
      }, 0),
    [deviceTotals, yieldDeviceMap]
  );

  // Pivot daily tons/hour data: { day, [deviceEsn]: tph }
  const tphChartData = useMemo(() => {
    const dayMap = new Map<string, Record<string, number | string>>();
    for (const entry of chartData) {
      dayMap.set(entry.day as string, { day: entry.day });
    }
    for (const row of dailyYield) {
      if (!visibleDevices.has(row.device_id)) continue;
      if (!dayMap.has(row.day)) {
        dayMap.set(row.day, { day: row.day });
      }
      const entry = dayMap.get(row.day)!;
      entry[row.device_id] = Math.round((row.tph_value ?? 0) * 100) / 100;
    }
    return Array.from(dayMap.values()).sort((a, b) =>
      (a.day as string).localeCompare(b.day as string)
    );
  }, [dailyYield, visibleDevices, chartData]);

  // Pivot daily mass (from same API as yield): { day, [deviceEsn]: mass }
  const massChartData = useMemo(() => {
    const dayMap = new Map<string, Record<string, number | string>>();
    for (const entry of chartData) {
      dayMap.set(entry.day as string, { day: entry.day });
    }
    for (const row of dailyYield) {
      if (!visibleDevices.has(row.device_id)) continue;
      if (!dayMap.has(row.day)) {
        dayMap.set(row.day, { day: row.day });
      }
      const entry = dayMap.get(row.day)!;
      const m = row.mass_value ?? 0;
      entry[row.device_id] = Math.round(m * 100) / 100;
    }
    return Array.from(dayMap.values()).sort((a, b) =>
      (a.day as string).localeCompare(b.day as string)
    );
  }, [dailyYield, visibleDevices, chartData]);

  const toggleDevice = (esn: string) => {
    setVisibleDevices((prev) => {
      const next = new Set(prev);
      if (next.has(esn)) next.delete(esn);
      else next.add(esn);
      return next;
    });
  };

  const formatDay = (day: string) => {
    const d = new Date(day + "T12:00:00");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const initialLoading = loading && devices.length === 0;
  const noDataAfterLoad =
    !loading && !rangeError && devices.length === 0 && validRange != null;

  // In "inside fields" mode an empty result usually means the work happened on
  // parcels that were never drawn, not that the devices were idle — say so,
  // otherwise the zero reads as a data outage.
  const noDataMessage = insideFieldsOnly
    ? (lang.analytics?.noDataInsideFields ??
      "No harvest recorded inside your field boundaries for this period.")
    : (lang.analytics?.noDataForPeriod ?? "No data for the selected period.");
  const noYieldMessage = insideFieldsOnly
    ? (lang.analytics?.noDataInsideFields ??
      "No harvest recorded inside your field boundaries for this period.")
    : (lang.analytics?.noYieldData ?? "No yield data for the selected period.");

  return (
    <Root>
      <TopBar>
        <RangeSelector>
          {(["3d", "5d", "7d", "custom"] as const).map((m) => (
            <RangeButton
              key={m}
              active={rangeMode === m}
              onClick={() => setRangeMode(m)}
            >
              {m === "3d" && (lang.analytics?.range3d ?? "3 days")}
              {m === "5d" && (lang.analytics?.range5d ?? "5 days")}
              {m === "7d" && (lang.analytics?.range7d ?? "7 days")}
              {m === "custom" && (lang.analytics?.rangeCustom ?? "Custom")}
            </RangeButton>
          ))}
          {rangeMode === "custom" && (
            <>
              <CustomDateInput
                type="date"
                value={customFrom}
                max={customTo || toDateInputValue(today)}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
              <span style={{ color: "#899093" }}>–</span>
              <CustomDateInput
                type="date"
                value={customTo}
                min={customFrom}
                max={toDateInputValue(today)}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </>
          )}
        </RangeSelector>
        <RangeSelector>
          <RangeButton
            active={!insideFieldsOnly}
            onClick={() => setInsideFieldsOnly(false)}
          >
            {lang.analytics?.scopeAllPoints ?? "All points"}
          </RangeButton>
          <RangeButton
            active={insideFieldsOnly}
            onClick={() => setInsideFieldsOnly(true)}
            title={
              lang.analytics?.scopeInsideFieldsHint ??
              "Count only points that fall inside your field boundaries"
            }
          >
            {lang.analytics?.scopeInsideFields ?? "Inside fields"}
          </RangeButton>
        </RangeSelector>
        <RefreshButton
          onClick={fetchData}
          disabled={loading || yieldLoading || !validRange}
          title={lang.analytics?.refreshButton ?? "Refresh"}
        >
          ↻ {lang.analytics?.refreshButton ?? "Refresh"}
        </RefreshButton>
      </TopBar>

      {rangeError && <RangeErrorText>{rangeError}</RangeErrorText>}

      {initialLoading && (
        <LoadingLayout>
          <Spinner />
        </LoadingLayout>
      )}

      {noDataAfterLoad && (
        <EmptyState>
          {lang.analytics?.dashboardEmpty ??
            "No device data available for this period."}
        </EmptyState>
      )}

      {!initialLoading && !noDataAfterLoad && (
      <ChartsGrid>
      {/* Harvested Area Chart Card */}
      <ChartCard>
        <CardHeader>
          <Title style={{ marginBottom: 0 }}>
            {lang.analytics?.harvestedAreaTitle ?? "Harvested area per day"} ({areaLabel})
          </Title>
        </CardHeader>

        {/* Device chips */}
        <ChipRow>
          {devices.map((d) => (
            <Chip
              key={d.esn}
              active={visibleDevices.has(d.esn)}
              chipColor={colorMap.get(d.esn) ?? "#68ad18"}
              onClick={() => toggleDevice(d.esn)}
            >
              <ColorDot color={colorMap.get(d.esn) ?? "#68ad18"} />
              {d.name}
            </Chip>
          ))}
        </ChipRow>

        {loading ? (
          <LoadingLayout>
            <Spinner />
          </LoadingLayout>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f3" />
              <XAxis
                dataKey="day"
                tickFormatter={formatDay}
                tick={{ fontSize: 11, fill: "#899093" }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: "#899093" }} width={50} />
              <Tooltip
                labelFormatter={formatDay}
                formatter={(value: number, name: string) => {
                  const dev = devices.find((d) => d.esn === name);
                  return [`${value.toFixed(1)} ${areaLabel}`, dev?.name ?? name];
                }}
                contentStyle={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontSize: 13,
                  borderRadius: 6,
                }}
              />
              <Legend
                formatter={(value: string) => {
                  const dev = devices.find((d) => d.esn === value);
                  return dev?.name ?? value;
                }}
                wrapperStyle={{ fontSize: 12 }}
              />
              {devices
                .filter((d) => visibleDevices.has(d.esn))
                .map((d) => (
                  <Bar
                    key={d.esn}
                    dataKey={d.esn}
                    stackId="area"
                    fill={colorMap.get(d.esn)}
                    radius={[2, 2, 0, 0]}
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState>
            {noDataMessage}
          </EmptyState>
        )}
      </ChartCard>

      {/* Harvested mass per day (stacked bars — same integration as yield) */}
      <ChartCard>
        <CardHeader>
          <Title style={{ marginBottom: 0 }}>
            {lang.analytics?.harvestedMassTitle ?? "Harvested mass per day"}{" "}
            ({massUnitLabel})
          </Title>
          <RefreshButton
            onClick={fetchData}
            disabled={loading || yieldLoading}
            title="Refresh"
          >
            ↻ {lang.analytics?.refreshButton ?? "Refresh"}
          </RefreshButton>
        </CardHeader>

        <ChipRow>
          {devices.map((d) => (
            <Chip
              key={d.esn}
              active={visibleDevices.has(d.esn)}
              chipColor={colorMap.get(d.esn) ?? "#68ad18"}
              onClick={() => toggleDevice(d.esn)}
            >
              <ColorDot color={colorMap.get(d.esn) ?? "#68ad18"} />
              {d.name}
            </Chip>
          ))}
        </ChipRow>

        {yieldLoading ? (
          <LoadingLayout>
            <Spinner />
          </LoadingLayout>
        ) : massChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={massChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f3" />
              <XAxis
                dataKey="day"
                tickFormatter={formatDay}
                tick={{ fontSize: 11, fill: "#899093" }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: "#899093" }} width={50} />
              <Tooltip
                labelFormatter={formatDay}
                formatter={(value: number, name: string) => {
                  const dev = devices.find((d) => d.esn === name);
                  return [`${value.toFixed(2)} ${massUnitLabel}`, dev?.name ?? name];
                }}
                contentStyle={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontSize: 13,
                  borderRadius: 6,
                }}
              />
              <Legend
                formatter={(value: string) => {
                  const dev = devices.find((d) => d.esn === value);
                  return dev?.name ?? value;
                }}
                wrapperStyle={{ fontSize: 12 }}
              />
              {devices
                .filter((d) => visibleDevices.has(d.esn))
                .map((d) => (
                  <Bar
                    key={d.esn}
                    dataKey={d.esn}
                    stackId="mass"
                    fill={colorMap.get(d.esn)}
                    radius={[2, 2, 0, 0]}
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState>
            {noYieldMessage}
          </EmptyState>
        )}
      </ChartCard>

      {/* Average Yield Chart Card */}
      <ChartCard>
        <Title>
          {lang.analytics?.avgYieldTitle ?? "Average yield per day"}{yieldUnit ? ` (${yieldUnit})` : ""}
        </Title>

        {yieldLoading ? (
          <LoadingLayout>
            <Spinner />
          </LoadingLayout>
        ) : yieldChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={yieldChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f3" />
              <XAxis
                dataKey="day"
                tickFormatter={formatDay}
                tick={{ fontSize: 11, fill: "#899093" }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: "#899093" }} width={50} />
              <Tooltip
                labelFormatter={formatDay}
                formatter={(value: number, name: string) => {
                  const dev = devices.find((d) => d.esn === name);
                  const yd = yieldDeviceMap.get(name);
                  const label = yd?.calCoeffSource === "fallback"
                    ? `${(dev?.name ?? name)} ⚠`
                    : (dev?.name ?? name);
                  return [`${value.toFixed(2)} ${yieldUnit}`, label];
                }}
                contentStyle={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontSize: 13,
                  borderRadius: 6,
                }}
              />
              <Legend
                formatter={(value: string) => {
                  const dev = devices.find((d) => d.esn === value);
                  const yd = yieldDeviceMap.get(value);
                  const name = dev?.name ?? value;
                  return yd?.calCoeffSource === "fallback" ? `${name} ⚠` : name;
                }}
                wrapperStyle={{ fontSize: 12 }}
              />
              {devices
                .filter((d) => visibleDevices.has(d.esn))
                .map((d) => (
                  <Line
                    key={d.esn}
                    type="monotone"
                    dataKey={d.esn}
                    stroke={colorMap.get(d.esn)}
                    strokeWidth={2}
                    dot={{ r: 4, fill: colorMap.get(d.esn), strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState>
            {noYieldMessage}
          </EmptyState>
        )}
      </ChartCard>

      {/* Tons per Hour Chart Card */}
      <ChartCard>
        <Title>
          {lang.analytics?.tonsPerHourTitle ?? "Yield per hour per day"}{tphUnit ? ` (${tphUnit})` : ""}
        </Title>

        {yieldLoading ? (
          <LoadingLayout>
            <Spinner />
          </LoadingLayout>
        ) : tphChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={tphChartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f3" />
              <XAxis
                dataKey="day"
                tickFormatter={formatDay}
                tick={{ fontSize: 11, fill: "#899093" }}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fontSize: 11, fill: "#899093" }} width={50} />
              <Tooltip
                labelFormatter={formatDay}
                formatter={(value: number, name: string) => {
                  const dev = devices.find((d) => d.esn === name);
                  const yd = yieldDeviceMap.get(name);
                  const label = yd?.calCoeffSource === "fallback"
                    ? `${(dev?.name ?? name)} ⚠`
                    : (dev?.name ?? name);
                  return [`${value.toFixed(2)} ${tphUnit || "t/h"}`, label];
                }}
                contentStyle={{
                  fontFamily: "Nunito Sans, sans-serif",
                  fontSize: 13,
                  borderRadius: 6,
                }}
              />
              <Legend
                formatter={(value: string) => {
                  const dev = devices.find((d) => d.esn === value);
                  const yd = yieldDeviceMap.get(value);
                  const name = dev?.name ?? value;
                  return yd?.calCoeffSource === "fallback" ? `${name} ⚠` : name;
                }}
                wrapperStyle={{ fontSize: 12 }}
              />
              {devices
                .filter((d) => visibleDevices.has(d.esn))
                .map((d) => (
                  <Line
                    key={d.esn}
                    type="monotone"
                    dataKey={d.esn}
                    stroke={colorMap.get(d.esn)}
                    strokeWidth={2}
                    dot={{ r: 4, fill: colorMap.get(d.esn), strokeWidth: 0 }}
                    activeDot={{ r: 6 }}
                    connectNulls={false}
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState>
            {noYieldMessage}
          </EmptyState>
        )}
      </ChartCard>
      </ChartsGrid>
      )}

      {/* Combined Device Summary Table */}
      {!initialLoading && !noDataAfterLoad && devices.length > 0 && (
      <Card>
        <Title>{lang.analytics?.deviceSummaryTitle ?? "Device summary"}</Title>
        <Table>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>{lang.analytics?.deviceColumn ?? "Device"}</Th>
              <Th>
                {lang.analytics?.widthColumn ?? "Width"} ({lengthUnit})
              </Th>
              <Th style={{ textAlign: "right" }}>
                {lang.analytics?.areaColumn ?? "Area"} ({areaLabel})
              </Th>
              <Th style={{ textAlign: "right" }}>
                {lang.analytics?.massHarvestedColumn ?? "Mass harvested"}{" "}
                ({massUnitLabel})
              </Th>
            </tr>
          </thead>
          <tbody>
            {deviceTotals.map((d, i) => {
              const yd = yieldDeviceMap.get(d.esn);
              return (
                <tr key={d.esn}>
                  <Td>{i + 1}</Td>
                  <Td bold>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <ColorDot color={colorMap.get(d.esn) ?? "#68ad18"} />
                      {d.name}
                    </div>
                  </Td>
                  <Td>
                    {roundLengthForDisplay(
                      convertLength(d.width, LengthUnit.Meter, lengthUnit)
                    ).toFixed(1)}
                  </Td>
                  <Td bold style={{ textAlign: "right" }}>
                    {d.totalArea.toFixed(1)}
                  </Td>
                  <Td style={{ textAlign: "right" }}>
                    {yieldLoading ? (
                      "—"
                    ) : yd ? (
                      <>
                        {yd.calCoeffSource === "fallback" ? "~" : ""}
                        {(yd.totalMass ?? 0).toFixed(2)}
                        {yd.calCoeffSource === "fallback" && (
                          <UncalibratedBadge>uncalibrated</UncalibratedBadge>
                        )}
                        {isJohnDeereCoeffSource(yd.calCoeffSource) && (
                          <JohnDeereBadge>
                            {(lang.analytics as Record<string, string | undefined> | undefined)
                              ?.coeffSourceJohnDeere ?? "John Deere"}
                          </JohnDeereBadge>
                        )}
                      </>
                    ) : (
                      "—"
                    )}
                  </Td>
                </tr>
              );
            })}
            <tr>
              <Td />
              <Td bold>{lang.analytics?.totalRow ?? "Total"}</Td>
              <Td />
              <Td bold style={{ textAlign: "right" }}>
                {grandTotal.toFixed(1)}
              </Td>
              <Td bold style={{ textAlign: "right" }}>
                {yieldLoading ? "—" : grandTotalMass.toFixed(2)}
              </Td>
            </tr>
          </tbody>
        </Table>
      </Card>
      )}
    </Root>
  );
};

export default observer(Dashboard);
